import { endOfDay, startOfDay } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { defineStore } from 'pinia';
import { computed, reactive, ref, shallowRef } from 'vue';
import { Currency } from '@/constants';
import { applyDatePeriodPresetToBetDate } from '@/helpers/applyDatePeriodPresetToBetDate';
import { ReportItem, ReportStats } from '@/models/report';
import { createReportPageHeaders } from '@/pages/accounting/report/ReportPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { reportService } from '@/services/reportService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IPageOptions } from '@/types/page';
import { IReportFilters, IReportStatsApi } from '@/types/report';
import type { DataTableHeader } from '@/types/vuetify';

const PRIMARY_STORE_ID = 'report';

const defaultStatsApi: IReportStatsApi = {};

function normalizeStats(stats: IReportStatsApi | IReportStatsApi[] | undefined | null, requestCurrencyId: number): ReportStats {
    if (Array.isArray(stats)) {
        return new ReportStats(stats[0] ?? defaultStatsApi, requestCurrencyId);
    }
    return new ReportStats(stats ?? defaultStatsApi, requestCurrencyId);
}

function createReportStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<ReportItem[]>([]);
    const stats = shallowRef<ReportStats>(new ReportStats(defaultStatsApi));
    const total = ref(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IReportFilters>({
        datePeriod: {
            value: 'range',
            title: 'bookBalance.datePeriod.title',
            allowSaveState: true,
            disableClear: true,
        },
        betDate: {
            value: [startOfDay(new Date()), endOfDay(new Date())],
            title: 'bookBalance.betDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        betUserId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
        },
        playerCurrencyId: {
            value: Currency.Euro,
            title: 'common.playerCurrency',
            allowSaveState: true,
            disableClear: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [],
    });

    // --- reset ---
    function resetFilters() {
        filters.datePeriod.value = 'range';
        filters.betDate.value = [startOfDay(new Date()), endOfDay(new Date())];
        filters.betUserId.value = null;
        filters.playerCurrencyId.value = Currency.Euro;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createReportPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: PRIMARY_STORE_ID,
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersCopy = cloneDeep(filters);
            applyDatePeriodPresetToBetDate(filtersCopy);

            const params = {
                filters: filtersCopy,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: ['Stats'],
                requiredDictionaries: ['CurrencyAll', 'Player'],
                requestKey: `report:${storeId}`,
            };

            const response = await reportService.list(params);
            const requestCurrencyId = filtersCopy.playerCurrencyId.value;
            items.value = (response.data.rows ?? []).map((row) => new ReportItem(row, requestCurrencyId));
            stats.value = normalizeStats(response.data.stats, requestCurrencyId);
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        stats.value = new ReportStats(defaultStatsApi);
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        stats,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        hasRefresh: true,
        hasExport: false,
    };
}

export function useReportStore(storeId: string) {
    const useStore = defineStore(storeId, () => createReportStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, PRIMARY_STORE_ID),
    });
    return useStore();
}

export type ReportStoreSetup = ReturnType<typeof useReportStore>;
